import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { Banner, EmptyState, LoadingPage, Tag } from '../../components/ui';
import { IncomeExpenseChart } from './IncomeExpenseChart';
import { Metric, Segmented, pctLabel, usd } from './shared';

type Granularity = 'monthly' | 'quarterly' | 'annual';
type Fund = 'all' | 'operating' | 'replacement';

export function AtAGlance({ onGoto }: { onGoto: (tab: string) => void }) {
  const [granularity, setGranularity] = useState<Granularity>('monthly');
  const [fund, setFund] = useState<Fund>('all');
  const state = useAsync(() => api.finDashboard(granularity, fund), [granularity, fund]);
  const data = state.data;

  if (!data) return state.loading ? <LoadingPage label="Loading the dashboard…" /> : null;

  if (!data.hasData || !data.latest || !data.position || !data.month || !data.ytd) {
    return (
      <div>
        {data.warnings.map((w) => (
          <Banner key={w.message} kind={w.level === 'warning' ? 'warn' : 'info'}>{w.message}</Banner>
        ))}
        <EmptyState
          title="No approved monthly statement yet."
          hint="Upload the monthly financial report, review what was read, and approve it. This page fills in from approved statements only."
        />
        <button type="button" className="btn" onClick={() => onGoto('statements')}>Go to Monthly Statements</button>
      </div>
    );
  }

  const { latest, position: p, month: m, ytd } = data;
  const ytdVsBudget = ytd.expenses - ytd.budgetExpenses;
  const below = p.belowMinimum === true;

  return (
    <div>
      <div className="row between" style={{ marginBottom: 8 }}>
        <div>
          <strong>{latest.periodLabel}</strong>
          <span className="mut">
            {' '}· approved statement{latest.version > 1 ? `, version ${latest.version}` : ''}
          </span>
        </div>
        <Tag tone={latest.reconciled ? 'ok' : 'warn'}>{latest.reconciled ? 'Cash reconciled' : 'Cash not reconciled'}</Tag>
      </div>

      {data.warnings.map((w) => (
        <div key={w.message} style={{ marginBottom: 8 }}>
          <Banner kind={w.level === 'warning' ? 'warn' : 'info'}>{w.message}</Banner>
        </div>
      ))}

      <div className="card">
        <div className="card-head"><h3>Financial position</h3><span className="mut">as of {latest.periodEnd ?? latest.periodLabel}</span></div>
        <div className="stat-grid">
          <Metric label="Operating cash" value={usd(p.operatingCash)} />
          <Metric label="Replacement (reserve) cash" value={usd(p.replacementCash)} />
          <Metric label="Receivables (A/R)" value={usd(p.receivables)} hint="Owed to the association, not cash" />
          <Metric label="Payables (A/P)" value={usd(p.payables)} />
          <Metric
            label="Available operating liquidity"
            value={usd(p.availableOperatingLiquidity)}
            hint="Operating cash + deposits in transit − payables"
            tone={p.availableOperatingLiquidity < 0 || below ? 'bad' : undefined}
            estimate
          />
          <Metric
            label="Operating runway"
            value={p.runwayMonths === null ? '—' : `${p.runwayMonths.toFixed(1)} mo`}
            hint={`At ${usd(p.avgMonthlyOperatingExpense)} average monthly operating expense`}
            tone={p.runwayMonths !== null && p.runwayMonths < 1 ? 'bad' : undefined}
            estimate
          />
          <Metric label="Replacement fund balance" value={usd(p.fundBalanceReplacement)} />
          <Metric label="Operating fund balance" value={usd(p.fundBalanceOperating)} />
        </div>
        {p.minOperatingCash !== null && (
          <p className="mut" style={{ marginBottom: 0 }}>
            Board minimum operating cash: {usd(p.minOperatingCash)}. {below ? 'Liquidity is below this minimum.' : 'Liquidity is above this minimum.'}
          </p>
        )}
      </div>

      <div className="card">
        <div className="card-head"><h3>{latest.periodLabel} activity</h3></div>
        <div className="stat-grid">
          <Metric
            label="Expected assessments"
            value={usd(m.expectedAssessments)}
            hint={m.expectedAssessments === null ? 'Add units and dues in Settings' : data.settingsConfirmed ? 'Units × dues from board settings' : 'Settings not yet confirmed by the board'}
            estimate
          />
          <Metric label="Billed assessments" value={usd(m.billedAssessments)} hint="From the statement" />
          <Metric label="Payments received" value={usd(m.paymentsReceived)} hint="Owner receipts, all charge types" />
          <Metric label="Cash collection rate" value={pctLabel(m.collectionRate)} hint="Receipts ÷ amounts due" estimate />
          <Metric label="Operating expenses" value={usd(m.operatingExpenses)} hint={`Budget ${usd(m.operatingExpenseBudget)}`} />
          <Metric
            label="Budget variance"
            value={usd(m.operatingBudgetVariance)}
            hint={m.operatingBudgetVariance < 0 ? 'Over budget this month' : 'Under budget this month'}
            tone={m.operatingBudgetVariance < 0 ? 'bad' : 'ok'}
          />
          <Metric label="Reserve contributions" value={usd(m.reserveContributions)} />
          <Metric label="One-time expenditures" value={usd(m.oneTimeExpenditures)} hint="Replacement-fund and capital spending" />
          <Metric
            label="Net operating cash flow"
            value={usd(m.netOperatingCashFlow)}
            hint={m.netOperatingCashFlow === null ? 'Needs the previous month approved too' : 'Change in operating cash from last month'}
            tone={m.netOperatingCashFlow !== null && m.netOperatingCashFlow < 0 ? 'bad' : undefined}
            estimate
          />
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h3>Year to date</h3></div>
        <div className="stat-grid">
          <Metric label="Revenue" value={usd(ytd.revenue)} hint={`Budget ${usd(ytd.budgetRevenue)}`} />
          <Metric label="Expenses" value={usd(ytd.expenses)} hint={`Budget ${usd(ytd.budgetExpenses)}`} tone={ytdVsBudget > 0 ? 'bad' : undefined} />
          <Metric label="Surplus / (deficit)" value={usd(ytd.surplus)} tone={ytd.surplus < 0 ? 'bad' : 'ok'} hint="Both funds combined" />
          <Metric label="Operating surplus" value={usd(ytd.operatingSurplus)} tone={ytd.operatingSurplus < 0 ? 'bad' : 'ok'} />
          <Metric label="Replacement surplus" value={usd(ytd.replacementSurplus)} tone={ytd.replacementSurplus < 0 ? 'bad' : 'ok'} hint="Negative when reserves fund projects" />
          {ytd.priorYear && (
            <Metric label={`Same month, prior year (${ytd.priorYear.label})`} value={usd(ytd.priorYear.expenses)} hint="Expenses year to date" />
          )}
        </div>
        <p className="mut" style={{ marginBottom: 0 }}>
          Year-to-date figures are read from the statement, never added up across months.
        </p>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Income vs. expenses</h3>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Segmented
              label="Period"
              value={granularity}
              onChange={setGranularity}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
                { value: 'annual', label: 'Annual' },
              ]}
            />
            <Segmented
              label="Fund"
              value={fund}
              onChange={setFund}
              options={[
                { value: 'all', label: 'Both funds' },
                { value: 'operating', label: 'Operating' },
                { value: 'replacement', label: 'Replacement' },
              ]}
            />
          </div>
        </div>
        {data.series && data.series.length > 0 ? (
          <IncomeExpenseChart points={data.series} granularity={granularity} />
        ) : (
          <p className="mut">No data for this view.</p>
        )}
        {data.seriesCoverage && (
          <p className="mut" style={{ marginBottom: 0 }}>
            Built from {data.seriesCoverage.months} approved statement{data.seriesCoverage.months === 1 ? '' : 's'} ({data.seriesCoverage.first}
            {data.seriesCoverage.months > 1 ? ` to ${data.seriesCoverage.last}` : ''}). Months without an approved statement are not estimated.
          </p>
        )}
      </div>

      <p className="mut">
        <Tag tone="info">Estimate</Tag> marks a figure calculated from statement data and your settings. Everything else is read directly from an approved statement.
      </p>
    </div>
  );
}
